/**
 * @component ErrorBoundary
 * @description The router's errorElement — the panel a user sees when a route fails
 * @stateType local (route error state from React Router)
 * @accessibility WCAG AA compliant with proper error announcements and recovery options
 * @performance Optimized error display with minimal re-renders and proper cleanup
 *
 * @businessContext
 * This component provides a comprehensive error handling system for Alveary,
 * capturing both React component errors and routing errors. It presents users with clear
 * error information and recovery options.
 *
 * @dataFlow
 * Render Errors -> React Router's boundary -> useRouteError -> Error Display
 * Route Errors -> React Router -> useRouteError -> Error Display
 * User Actions -> Navigation/Reload -> System Recovery
 *
 * @dependencies
 * - React Router for route-specific error handling
 * - Shared `ui-*` design tokens for consistent visual presentation
 *
 * Reporting is not done here: the root error callbacks wired in main.tsx are
 * the single reporter for every failure React observes.
 */

import { memo, useCallback, type ReactElement } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import DocumentTitle from '@/components/common/DocumentTitle'

/**
 * Props interface for ErrorDisplay component.
 *
 * @interface ErrorDisplayProps
 * @property {Error | null} error - Error object to display
 * @businessContext Provides error information for user feedback and debugging
 */
interface ErrorDisplayProps {
  /**
   * @property {Error | null} error - The error object with message and stack trace
   * @businessContext Contains error details for user display and developer debugging
   * @dataSource Error boundary component or route error handling
   */
  error: Error | null
}

/**
 * Error display component for showing user-friendly error messages
 *
 * @component ErrorDisplay
 * @param {ErrorDisplayProps} props - Component properties
 * @returns {JSX.Element} Error display with recovery options
 * @businessLogic Presents error information with recovery actions in consistent UI
 * @accessibility Includes proper ARIA attributes and keyboard navigation
 */
const ErrorDisplay: React.FC<ErrorDisplayProps> = memo(({ error }) => {
  /**
   * Handle navigation to home page
   *
   * @callback handleReturnHome
   * @businessLogic Provides recovery option by navigating to safe application state
   * @sideEffects Changes window location to home page
   */
  const handleReturnHome = useCallback(() => {
    window.location.href = '/'
  }, [])

  /**
   * Handle page reload for error recovery
   *
   * @callback handleReload
   * @businessLogic Attempts to recover from error by reloading the current page
   * @sideEffects Reloads the current browser page
   */
  const handleReload = useCallback(() => {
    window.location.reload()
  }, [])

  return (
    <div
      className="min-h-screen bg-ui-canvas flex items-center justify-center p-4"
      role="alert"
      aria-live="assertive"
      aria-labelledby="error-title"
      data-testid="error-display"
    >
      <div className="relative z-10 max-w-2xl w-full">
        <div
          className="rounded-lg border border-ui-error/40 bg-ui-surface p-8"
          data-testid="error-container"
        >
          {/* Error header */}
          <div
            className="flex items-center space-x-4 mb-6"
            data-testid="error-header"
          >
            <div
              className="w-12 h-12 flex items-center justify-center"
              role="presentation"
              aria-hidden="true"
            >
              <span className="text-title text-ui-error">⚠</span>
            </div>
            <h1
              id="error-title"
              className="text-title font-sans text-ui-error"
              data-testid="error-title"
            >
              System error
            </h1>
          </div>

          {/* Error message */}
          <div className="space-y-4">
            <div
              className="bg-ui-canvas p-4 rounded-md border border-ui-error/30"
              data-testid="error-message"
            >
              <p
                className="text-body text-ui-text"
                role="text"
                aria-describedby="error-title"
              >
                {error?.message || 'An unexpected error occurred'}
              </p>
            </div>

            {/* Error details */}
            {error?.stack && (
              <details
                className="cursor-pointer"
                data-testid="error-details"
              >
                <summary
                  className="text-label text-ui-text-muted hover:text-ui-now transition-colors"
                  role="button"
                  aria-expanded="false"
                  tabIndex={0}
                >
                  View technical details
                </summary>
                <pre
                  className="mt-2 p-4 bg-ui-canvas font-mono text-code text-ui-text-muted overflow-x-auto rounded-md border border-ui-hairline"
                  role="log"
                  aria-label="Error stack trace"
                  data-testid="error-stack"
                >
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Action buttons */}
            <div
              className="flex gap-4 mt-6"
              role="group"
              aria-label="Recovery actions"
              data-testid="error-actions"
            >
              <button
                onClick={handleReturnHome}
                className="px-6 py-2 rounded-md bg-ui-canvas border border-ui-now text-body text-ui-now hover:bg-ui-now hover:text-ui-on-accent transition-all"
                aria-label="Return to home page"
                data-testid="return-home-button"
              >
                Return home
              </button>
              <button
                onClick={handleReload}
                className="px-6 py-2 rounded-md bg-ui-canvas border border-ui-memory text-body text-ui-memory hover:bg-ui-memory hover:text-ui-on-accent transition-all"
                aria-label="Reload current page"
                data-testid="reload-button"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

ErrorDisplay.displayName = 'ErrorDisplay'

/**
 * Router error boundary component for handling route-specific errors
 *
 * @component ErrorBoundary
 * @description React Router error boundary with specialized 404 handling
 * @stateType local (route error state from React Router)
 * @accessibility WCAG AA compliant with proper error announcements
 * @performance Minimal re-renders, memoized error display components
 *
 * @businessContext
 * Handles route-specific errors including 404 pages and navigation errors.
 * Provides users with contextual error information and recovery options.
 *
 * @returns {JSX.Element} Appropriate error display based on error type
 */
const ErrorBoundary: React.FC = memo(() => {
  const error = useRouteError()

  /**
   * Handle navigation to home page from 404
   *
   * @callback handleReturnToDashboard
   * @businessLogic Provides recovery from 404 errors by navigating to main dashboard
   * @sideEffects Changes window location to home page
   */
  const handleReturnToDashboard = useCallback(() => {
    window.location.href = '/'
  }, [])

  let panel: ReactElement

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      panel = (
        <div
          className="min-h-screen bg-ui-canvas flex items-center justify-center p-4"
          role="main"
          aria-labelledby="not-found-title"
          data-testid="not-found-page"
        >
          <div
            className="text-center"
            data-testid="not-found-content"
          >
            <h1
              id="not-found-title"
              className="text-display font-sans text-ui-error mb-4"
              role="heading"
              aria-level={1}
              data-testid="not-found-title"
            >
              404
            </h1>
            <p
              className="text-title text-ui-text-muted mb-8"
              role="text"
              data-testid="not-found-message"
            >
              Page not found in the system
            </p>
            <button
              onClick={handleReturnToDashboard}
              className="px-6 py-3 rounded-md bg-ui-surface border border-ui-now text-body text-ui-now hover:bg-ui-now hover:text-ui-on-accent transition-all"
              aria-label="Return to main dashboard"
              data-testid="return-to-dashboard-button"
            >
              Return to dashboard
            </button>
          </div>
        </div>
      )
    } else {
      panel = <ErrorDisplay error={new Error(`${error.status}: ${error.statusText}`)} />
    }
  // eslint-disable-next-line no-restricted-syntax -- React's error boundary also receives native render errors outside the API client.
  } else if (error instanceof Error) {
    panel = <ErrorDisplay error={error} />
  } else {
    panel = <ErrorDisplay error={new Error('Unknown error')} />
  }

  // This element replaces AppLayout outright on a root error, taking the app's
  // usual title owner with it — so the error path mounts the only other one.
  return (
    <>
      <DocumentTitle />
      {panel}
    </>
  )
})

ErrorBoundary.displayName = 'ErrorBoundary'

export default ErrorBoundary
